import React, {useCallback, useEffect, useRef, useState} from "react";
import {GroupedPackageItem} from "types/entities/GroupedPackageItem";
import {ServerImage} from "components/ServerImage";
import {getSelectedUpsell, translate} from "utils/utility";
import clsx from "clsx";
import {useTypedSelector} from "helpers/reducers";
import CombinationOfVariantPackageUpsellItemOpenButton from "pages/Front/Booking/components/PackageUpsell/components/CombinationOfVariantPackageUpsellItemOpenButton";
import CombinationOfVariantPackageUpsellItemItem from "pages/Front/Booking/components/PackageUpsell/CombinationOfVariantPackageUpsellItemItem";
import {useDispatch} from "useDispatch";
import {toast} from "react-toastify";
import BookingAction from "store/booking/BookingAction";
import {PackageItem} from "types/entities/PackageItem";
import {useTranslation} from "react-i18next";
import {useDebouncedCallback} from "use-debounce";
import AlertConfirmation from "pages/Front/Registration/components/AlertConfirmation";

export interface CombinationOfVariantAssignedUpsell {
    upsell_id: number;
    assigned_count: number;
}

interface CombinationOfVariantPackageUpsellItemProps {
    item: GroupedPackageItem;
    isSeller?: boolean;
}

const CombinationOfVariantPackageUpsellItem = (props: CombinationOfVariantPackageUpsellItemProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const isAnyUpsellSelected = props.item.items.some(packageItem => {
        return getSelectedUpsell(bookingProcess.upsell, packageItem) !== undefined;
    });
    const [isOptionsOpen, setIsOptionsOpen] = useState<boolean>(true);
    const contentRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [assignedUpsells, setAssignedUpsells] = useState<CombinationOfVariantAssignedUpsell[]>([]);
    const [showUpdateButton, setShowUpdateButton] = useState(false);

    const handleToggle = () => {
        setIsOptionsOpen(!isOptionsOpen);
    };

    const validationCount = () => {
        const totalVariantsAssigned = assignedUpsells.reduce((sum, upsell) => sum + (upsell.assigned_count ?? 0), 0);
        const totalVariantsMaximumCount = props.item.packageItemVariant?.maximum_count ?? 0;
        const totalVariantsMinimumCount = props.item.packageItemVariant?.minimum_count ?? 0;

        if (totalVariantsAssigned < totalVariantsMinimumCount) {
            toast.error(t("cannot order less", {minimum_value: totalVariantsMinimumCount}), {toastId: "minimumDriverCount"});
            return false;
        }
        if (totalVariantsAssigned > totalVariantsMaximumCount) {
            toast.error(t("We cannot take more people than: ") + totalVariantsMaximumCount, {toastId: "maximumDriverCount"});
            return false;
        }

        return true;
    };

    const onSetUpsell = () => {
        const isValid = validationCount();
        if (!isValid) {
            return;
        }

        assignedUpsells.map(upsell => {
            const selectedPackage = props.item.items.find(item => item.id === upsell.upsell_id);
            if (selectedPackage) {
                if (upsell.assigned_count > 0) {
                    dispatch(
                        BookingAction.pushUpsell({
                            packageItem: selectedPackage,
                            upsell_option: 0,
                            count: upsell.assigned_count,
                        })
                    );
                } else {
                    const currentUpsell = getSelectedUpsell(bookingProcess.upsell, selectedPackage);
                    if (currentUpsell) {
                        dispatch(BookingAction.removeUpsell(currentUpsell));
                    }
                }
            }
        });

        return isAnyUpsellSelected ? toast.success(t("Items updated")) : toast.success(t("Items added"));
    };

    // generate upsells to array for process
    const setAssigned = (packageItem: PackageItem, newUpsellCount: number) => {
        setAssignedUpsells(prevState => {
            const existIndex = prevState.findIndex(u => u.upsell_id === packageItem.id);
            if (existIndex > -1) {
                return prevState.map(u => (u.upsell_id === packageItem.id ? {...u, assigned_count: newUpsellCount} : u));
            } else {
                return [...prevState, {upsell_id: packageItem.id, assigned_count: newUpsellCount}];
            }
        });
    };

    // remove all
    const onRemoveUpsells = () => {
        AlertConfirmation({
            message: t("Are you sure you want to delete this upsell?"),
            onClick: () => {
                Promise.all(
                    assignedUpsells.map((upsell, index) => {
                        const selectedPackage = props.item.items.find(item => item.id === upsell.upsell_id);
                        if (selectedPackage) {
                            const currentUpsell = getSelectedUpsell(bookingProcess.upsell, selectedPackage);
                            if (currentUpsell) {
                                dispatch(BookingAction.removeUpsell(currentUpsell));
                            }
                        }

                        return Promise.resolve();
                    })
                ).then(() => {
                    setDefaultValuesForUpsells();
                    toast.success(t("Upsells removed"));
                });
            },
            title: t("Upsell removal"),
        });
    };

    // default values for upsells
    const setDefaultValuesForUpsells = () => {
        const allPersonsCount = bookingProcess.filter.adults + bookingProcess.filter.children;
        const nextAssigned = props.item.items.map((packageItem, index) => {
            const defaultUpsellCount =
                packageItem.packageItemVariant?.minimum_count && packageItem.packageItemVariant?.maximum_count
                    ? allPersonsCount >= packageItem.packageItemVariant.minimum_count && allPersonsCount <= packageItem.packageItemVariant.maximum_count
                        ? allPersonsCount
                        : allPersonsCount < packageItem.packageItemVariant.minimum_count
                          ? packageItem.packageItemVariant.minimum_count
                          : packageItem.packageItemVariant.maximum_count
                    : 0;
            const default_persons_count = index === 0 ? defaultUpsellCount : 0;

            return {
                upsell_id: packageItem.id,
                assigned_count: default_persons_count,
            };
        });

        setAssignedUpsells(nextAssigned);
    };

    // preset assigned upsells from state
    const presetFromStore = () => {
        const nextAssigned = props.item.items.map(packageItem => {
            const selectedUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem);
            return {
                upsell_id: packageItem.id,
                assigned_count: selectedUpsell ? selectedUpsell.count : 0,
            };
        });

        setAssignedUpsells(nextAssigned);
    };

    // comparison of assignedUpsells vs. current (rules: see assignment)
    const computeHasDifferences = useCallback((): boolean => {
        for (const packageItem of props.item.items) {
            const assigned = assignedUpsells.find(u => u.upsell_id === packageItem.id)?.assigned_count ?? 0;
            const currentUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem);
            const current = currentUpsell?.count ?? 0;

            // if current does not exist and assigned == 0 -> no difference
            if (!currentUpsell && assigned === 0) {
                continue;
            }
            // if current exists and assigned == 0 -> differs
            if (currentUpsell && assigned === 0) {
                return true;
            }
            // if the numbers don't match -> it differs
            if (assigned !== current) {
                return true;
            }
        }
        return false;
    }, [assignedUpsells, bookingProcess.upsell, props.item.items]);

    // debounce fo check difference
    const debouncedCheckDifferences = useDebouncedCallback(() => {
        const hasDiff = computeHasDifferences();
        setShowUpdateButton(hasDiff);
    }, 300);

    // only if upsell is in redux
    useEffect(() => {
        if (!isAnyUpsellSelected) {
            setShowUpdateButton(false);
            debouncedCheckDifferences.cancel();
            return;
        }

        debouncedCheckDifferences();
        return () => {
            debouncedCheckDifferences.cancel();
        };
    }, [isAnyUpsellSelected, computeHasDifferences, debouncedCheckDifferences]);

    // preset data for upsells
    useEffect(() => {
        if (isAnyUpsellSelected) {
            presetFromStore();
        } else {
            setDefaultValuesForUpsells();
        }
    }, []);

    return (
        <div className={"CombinationOfVariantPackageUpsellItem overflow-hidden w-full flex flex-col bg-white rounded-xl shadow"} ref={containerRef}>
            <div className={"w-full flex flex-col md:flex-row md:items-stretch"}>
                <div className={"flex flex-row w-full md:w-auto md:self-stretch md:flex-1"}>
                    <div
                        className={clsx(
                            "grow flex flex-col md:items-stretch w-full max-w-full",
                            props.isSeller ? "sm:w-[128px] sm:max-w-[128px]" : "md:w-[16rem] md:max-w-[16rem] h-full"
                        )}
                    >
                        <div
                            className={clsx(
                                "itemImage flex h-full overflow-hidden w-full grow rounded-t-lg md:rounded-tr-none",
                                props.isSeller ? "sm:max-w-[128px] aspect-auto" : "md:max-h-full aspect-[64/56] sm:max-h-[250px] md:max-w-[16rem]"
                            )}
                        >
                            {props.item?.packageItemVariant && props.item.packageItemVariant?.image && props.item.packageItemVariant.image !== "" && (
                                <ServerImage
                                    alt={"Package Item Image"}
                                    className={"w-full md:max-h-auto object-cover object-center"}
                                    src={props.item.packageItemVariant.image}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div
                    className={clsx(
                        "w-full text-black flex flex-col items-start justify-center",
                        props.isSeller ? "pl-2 pr-2 py-2 gap-y-1" : "px-4 lg:px-8 py-4 gap-y-2 md:gap-y-0"
                    )}
                    data-package-item-id={props.item.variant_id}
                >
                    <h3 className="text-black leading-tight text-base sm:text-xl lg:text-xl xl:text-2xl text-left font-bold flex-grow w-full">
                        {props.item?.packageItemVariant ? translate(props.item.packageItemVariant.name) : translate(props.item.name)}
                    </h3>

                    {props.item?.packageItemVariant && (
                        <div className={"flex justify-center items-center h-full w-full whitespace-pre-line text-black text-sm lg:text-base font-medium"}>
                            {props.item?.packageItemVariant.description_html ? (
                                <p className={"w-full"} dangerouslySetInnerHTML={{__html: translate(props.item?.packageItemVariant.description_html)}} />
                            ) : (
                                <p className={"w-full"}>{translate(props.item?.packageItemVariant.description)}</p>
                            )}
                        </div>
                    )}
                </div>
                <CombinationOfVariantPackageUpsellItemOpenButton
                    className={"!hidden md:!flex"}
                    handleToggle={handleToggle}
                    isAnyUpsellSelected={isAnyUpsellSelected}
                    isOptionsOpen={isOptionsOpen}
                    isSeller={props.isSeller}
                    setUpsell={onSetUpsell}
                    showRemoveButton={isAnyUpsellSelected}
                    setRemove={onRemoveUpsells}
                    showUpdateButton={showUpdateButton}
                />
            </div>

            <div
                className={
                    "flex flex-col items-center justify-between w-full divide-y overflow-hidden transition-[max-height] duration-500 ease-in-out transform-gpu will-change-auto"
                }
                style={{
                    maxHeight: isOptionsOpen ? `${contentRef.current?.scrollHeight}px` : "0px",
                }}
                ref={contentRef}
            >
                {props.item.items.map((packageItem, index) => {
                    return (
                        <CombinationOfVariantPackageUpsellItemItem
                            key={packageItem.id}
                            // minimum_persons_count={minimum_persons_count}
                            packageItem={packageItem}
                            isSeller={props.isSeller}
                            assignedUpsells={assignedUpsells}
                            setAssignedUpsells={setAssignedUpsells}
                        />
                    );
                })}
            </div>

            <CombinationOfVariantPackageUpsellItemOpenButton
                className={clsx("!flex md:!hidden", isOptionsOpen && "border-t md:border-t-0")}
                handleToggle={handleToggle}
                isAnyUpsellSelected={isAnyUpsellSelected}
                isOptionsOpen={isOptionsOpen}
                isSeller={props.isSeller}
                setUpsell={onSetUpsell}
                showRemoveButton={isAnyUpsellSelected}
                setRemove={onRemoveUpsells}
                showUpdateButton={showUpdateButton}
            />
        </div>
    );
};

export default CombinationOfVariantPackageUpsellItem;
